import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IoMdArrowRoundBack } from 'react-icons/io';
import { useHistory, useParams } from 'react-router-dom';
import { subscriptionSelectors } from '../../../app/resource/subscription/subscription.selectors';
import { invoiceSelectors } from '../../../app/resource/invoice/invoice.selectors';
import { IInvoice, IInvoiceLine } from '../../../app/resource/invoice/invoice.types';
import { fetchInvoiceDetail } from '../../../app/page/invoices/invoices.actions';
import { pageStateSelectors } from '../../../app/page/page-state/page-state.selectors';
import { authSelectors } from '../../../app/auth/auth.selectors';
import { userSelectors } from '../../../app/resource/user/user.selectors';
import { formatCentsAsCurrency } from '../../../utils/currency.utils';
import { formatUtcDateToUserTimeZone } from '../../../utils/date.utils';
import { useSelectWithParams } from '../../../utils/selector.utils';
import { PageLayout } from '../common/PageLayout';

export const InvoiceDetailPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const invoiceId = id ?? '';

  const invoice = useSelectWithParams(invoiceSelectors.selectResourceById, invoiceId) as IInvoice | null;
  const invoiceLines = invoice && Array.isArray(invoice.lines) ? invoice.lines : null;
  const subscriptions = useSelector(subscriptionSelectors.selectResourceList);
  const workspaceName = subscriptions[0]?.workspaceName ?? 'Workspace';
  const showLoader = useSelectWithParams(pageStateSelectors.selectPageInProgress, 'invoiceDetail');
  const loggedUserId = useSelector(authSelectors.selectLoggedUser);
  const loggedUser = useSelectWithParams(userSelectors.selectResourceById, loggedUserId ?? '');
  const userTimezone = loggedUser?.timezone ?? null;

  const [trackedInvoiceId, setTrackedInvoiceId] = useState(invoiceId);
  const [detailRequestStarted, setDetailRequestStarted] = useState(false);
  if (trackedInvoiceId !== invoiceId) {
    setTrackedInvoiceId(invoiceId);
    setDetailRequestStarted(false);
  }

  useEffect(() => {
    if (showLoader) {
      setDetailRequestStarted(true);
    }
  }, [showLoader]);

  const waitingForDetail = Boolean(invoiceId) && !invoiceLines && !detailRequestStarted;
  const showDetailLoader = showLoader || waitingForDetail;

  useEffect(() => {
    if (invoiceId) {
      dispatch(fetchInvoiceDetail(invoiceId));
    }
  }, [dispatch, invoiceId]);

  const formatLinePeriod = (line: IInvoiceLine) => {
    const start = formatUtcDateToUserTimeZone(line.periodStart, userTimezone);
    const end = formatUtcDateToUserTimeZone(line.periodEnd, userTimezone);
    if (start && end) {
      return `${start} – ${end}`;
    }
    return '—';
  };

  return (
    <PageLayout showLoader={showDetailLoader} showHide={true}>
      <div className="row mb-25">
        <div className="col-12">
          <div className="panel">
            <div className="no-border panel-header small-header">
              <h2 className="bold">Invoice details</h2>
              <div className="ml-auto flex aic jcr">
                <button
                  id="backbutton"
                  type="button"
                  className="btn btn-md btn-border white-bg"
                  style={{ width: 80, boxShadow: 'none' }}
                  onClick={() => history.push('/settings/subscription/invoices')}
                >
                  <IoMdArrowRoundBack />
                  <span className="ml-1">Back</span>
                </button>
              </div>
            </div>
            {!showDetailLoader &&
              (!invoice || !invoiceLines ? (
                <div className="panel-body pt-0">
                  <div className="border-radius-1 lightGray-bg p-2">
                    <div className="f-14 blueGray">Invoice not found.</div>
                  </div>
                </div>
              ) : (
                <div className="panel-body pt-0">
                  <div className="border-radius-1 lightGray-bg p-2">
                    <div className="row big-gap">
                      <div className="col-12">
                        <div className="panel invoice-detail">
                          <div className="panel-body">
                            <div className="flex jcsb aic mb-4">
                              <div>
                                <h1 className="f-20 mb-1">Number: {invoice.number}</h1>
                                <span
                                  className={`invoice-detail-status ${
                                    invoice.status === 'paid' ? 'lightGreen-bg green' : 'lightYellow-bg yellow'
                                  }`}
                                >
                                  {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                                </span>
                              </div>
                              <div>
                                {invoice.invoicePdf || invoice.hostedInvoiceUrl ? (
                                  <a
                                    className="btn primary small"
                                    href={invoice.invoicePdf || invoice.hostedInvoiceUrl || '#'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Download PDF
                                  </a>
                                ) : null}
                              </div>
                            </div>

                            <div className="invoice-detail-meta mb-4">
                              <div>
                                <div className="f-12 blueGray bold mb-1">BILL TO</div>
                                <div className="f-14 bold">{workspaceName}</div>
                              </div>
                              <div className="invoice-detail-dates">
                                <div className="invoice-detail-date">
                                  <span className="blueGray">Paid</span>
                                  <span>{formatUtcDateToUserTimeZone(invoice.paidAt, userTimezone) ?? '—'}</span>
                                </div>
                                <div className="invoice-detail-date">
                                  <span className="blueGray">Service period</span>
                                  <span>
                                    {formatUtcDateToUserTimeZone(invoice.periodStart, userTimezone) ?? '—'}
                                    {' – '}
                                    {formatUtcDateToUserTimeZone(invoice.periodEnd, userTimezone) ?? '—'}
                                  </span>
                                </div>
                              </div>
                            </div>

                            <div className="table-container invoice-detail-lines mb-3" role="table">
                              <div
                                className="flex-table header panel white mb-1 aic theme-gradient-2 py-1 px-2 flex aic"
                                role="rowgroup"
                              >
                                <div className="flex-row first" role="columnheader">
                                  DESCRIPTION
                                </div>
                                <div className="flex-row" role="columnheader">
                                  PERIOD
                                </div>
                                <div className="flex-row" role="columnheader">
                                  QTY
                                </div>
                                <div className="flex-row" role="columnheader">
                                  AMOUNT
                                </div>
                              </div>
                              {invoiceLines.length === 0 ? (
                                <div className="f-14 blueGray p-2">No line items.</div>
                              ) : (
                                invoiceLines.map((line, index) => (
                                  <div className="flex-table" role="rowgroup" key={`${line.description}-${index}`}>
                                    <div className="flex-row first" role="cell">
                                      <div className="f-14">
                                        {line.description || 'Subscription'}
                                        {line.proration ? <span className="blueGray f-12"> (proration)</span> : null}
                                      </div>
                                    </div>
                                    <div className="flex-row" role="cell">
                                      <div className="f-14">{formatLinePeriod(line)}</div>
                                    </div>
                                    <div className="flex-row" role="cell">
                                      <div className="f-14">{line.quantity ?? '—'}</div>
                                    </div>
                                    <div className="flex-row" role="cell">
                                      <div className="f-14 bold">
                                        {formatCentsAsCurrency(line.amount, invoice.currency)}
                                      </div>
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>

                            <div className="invoice-detail-totals">
                              <div className="invoice-detail-total-row f-14">
                                <span className="blueGray">Subtotal</span>
                                <span>{formatCentsAsCurrency(invoice.subtotal, invoice.currency)}</span>
                              </div>
                              {invoice.tax > 0 ? (
                                <div className="invoice-detail-total-row f-14">
                                  <span className="blueGray">Tax</span>
                                  <span>{formatCentsAsCurrency(invoice.tax, invoice.currency)}</span>
                                </div>
                              ) : null}
                              <div className="invoice-detail-total-row invoice-detail-grand-total f-14 bold">
                                <span>Total</span>
                                <span>{formatCentsAsCurrency(invoice.total, invoice.currency)}</span>
                              </div>
                              {invoice.amountOverpaid > 0 ? (
                                <div className="invoice-detail-total-row f-14">
                                  <span className="blueGray">Amount overpaid</span>
                                  <span>{formatCentsAsCurrency(invoice.amountOverpaid, invoice.currency)}</span>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
    </PageLayout>
  );
};
